import React, { useEffect, useState } from 'react'
import Drawer from '@mui/material/Drawer'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { formatMoney } from '../../../utils/amount'
import { formatPercent, formatGatewayValue } from './helpers'
import { useUiKit } from '../../../components/UiKit/UiKitContext'
import { BULK_TYPED_CHECK } from './SkipPaymentsModal'
import TypedCheck, { typedMatches } from '../../../components/ConfirmDialog/TypedCheck'

const formatGatewayBreakdown = (breakdown = {}, labels = {}) => {
  const entries = Object.entries(breakdown).filter(([, n]) => n > 0)
  if (!entries.length) return '—'
  return entries.map(([value, n]) => `${labels[value] || formatGatewayValue(value)} ${n}`).join(' · ')
}

const ShieldIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 2L4 5v6c0 5.25 3.4 9.74 8 11 4.6-1.26 8-5.75 8-11V5l-8-3z"
      stroke="var(--color-primary-main)"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path d="M9 12l2 2 4-4" stroke="var(--color-primary-main)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const SummaryRow = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-divider last:border-b-0">
    <span className="text-sm text-text-secondary">{label}</span>
    <span className="text-sm font-medium text-text-primary">{value}</span>
  </div>
)

const ConfirmDrawer = ({
  open,
  onClose,
  summary,
  submitting,
  onConfirm,
  dueDates = [],
  allowFutureDue = false,
  onToggleAllowFutureDue,
  showSkippedRow = true,
}) => {
  const {
    count,
    amount,
    avgForecast,
    gatewayBreakdown,
    gatewayLabels,
    bouncedIncludedCount,
    skippedCount,
    futureDueCount = 0,
    futureDueAmount = 0,
    hasBackupGateway,
  } = summary
  const { confirm: kitConfirm } = useUiKit()
  const [typedCount, setTypedCount] = useState('')

  useEffect(() => {
    if (!open) setTypedCount('')
  }, [open])

  const needsTypedCount = kitConfirm && count >= BULK_TYPED_CHECK
  const typedCountMatches = !needsTypedCount || typedMatches(typedCount, count)

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <div className="w-[420px] max-w-[90vw] h-full flex flex-col">
        <div className="flex items-center gap-3 px-6 py-5 border-b border-divider">
          <ShieldIcon />
          <div>
            <p className="text-lg font-semibold text-text-primary">Confirm Approval</p>
            <p className="text-xs text-text-secondary">These payments will be charged by the next processing run (within ~15 min).</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="rounded-xl border border-divider p-4">
            <SummaryRow
              label="Selected Payments"
              value={<span data-testid="confirm-selected-count">{count}</span>}
            />
            <SummaryRow label="Total Amount" value={formatMoney(amount)} />
            <SummaryRow label="AI Forecasted Success Rate" value={formatPercent(avgForecast)} />
            <SummaryRow label="Gateway Breakdown" value={formatGatewayBreakdown(gatewayBreakdown, gatewayLabels)} />
            <SummaryRow label="Previously Bounced Included" value={bouncedIncludedCount} />
            {showSkippedRow && <SummaryRow label="Skipped Payments" value={skippedCount} />}
          </div>

          {/* WHICH due dates this batch covers. A batch spanning several days looked
              identical to a single ordinary day in the old drawer — that ambiguity is what
              let 104 future-dated payments be approved on 2026-07-28 without anyone
              noticing. Future-dated days are labelled inline. */}
          {dueDates.length > 0 && (
            <div className="mt-4 rounded-xl border border-divider p-4" data-testid="due-date-breakdown">
              <p className="text-xs font-semibold text-text-secondary mb-2">Due dates in this batch</p>
              {dueDates.map((day) => (
                <div
                  key={day.dateKey}
                  data-testid={`due-date-${day.dateKey}`}
                  className="flex items-center justify-between py-1.5 border-b border-divider last:border-b-0"
                >
                  <span className="text-sm text-text-primary">
                    {day.label}
                    {day.isFuture && (
                      <span className="ml-2 text-xs font-medium text-warning-main">not yet due</span>
                    )}
                  </span>
                  <span className="text-sm text-text-secondary">
                    {day.count} · {formatMoney(day.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* The early-charge gate. Not-yet-due payments stay OUT of the batch (the headline
              count above already reflects that) until the operator explicitly opts in here —
              which is what sends `allowFutureDue` and satisfies the backend's not_yet_due
              guard. Deliberately a confirmation, not a blocker: processing the weekend early
              on a Friday is legitimate, doing it unknowingly is not. */}
          {futureDueCount > 0 && (
            <div
              className="mt-4 rounded-xl border border-warning-main/40 bg-warning-light/30 p-3.5"
              data-testid="future-due-callout"
            >
              <p className="text-xs font-semibold text-warning-main mb-1">
                {futureDueCount} selected payment{futureDueCount === 1 ? ' is' : 's are'} not due yet
                {' '}({formatMoney(futureDueAmount)})
              </p>
              <p className="text-xs text-text-primary leading-relaxed">
                {allowFutureDue
                  ? 'These will be charged BEFORE their due date.'
                  : "These are excluded from the batch. Tick below only if you mean to take these clients' money early — e.g. processing the weekend ahead on a Friday."}
              </p>
              <FormControlLabel
                className="mt-1"
                control={
                  <Checkbox
                    size="small"
                    checked={allowFutureDue}
                    onChange={(e) => onToggleAllowFutureDue?.(e.target.checked)}
                    inputProps={{ 'data-testid': 'allow-future-due-checkbox' }}
                  />
                }
                label={
                  <span className="text-xs text-text-primary">
                    Charge {futureDueCount} future-dated payment{futureDueCount === 1 ? '' : 's'} before{' '}
                    {futureDueCount === 1 ? 'its' : 'their'} due date
                  </span>
                }
              />
            </div>
          )}

          <div className="mt-4 rounded-xl border border-info-main/30 bg-info-light/30 p-3.5">
            <p className="text-xs font-semibold text-info-main mb-1">AI Recommendation</p>
            <p className="text-xs text-text-primary leading-relaxed">
              Forecasts are based on each client's historical payment performance. Review any Needs Review or
              low-forecast payments before confirming.
            </p>
          </div>

          {/* LawPay Part B (Task 6): at least one row in this batch will auto-fall-back to a
              backup processor if the primary soft-declines — the operator approving must
              never be unaware a backup may fire. */}
          {hasBackupGateway && (
            <div className="mt-4 rounded-xl border border-info-main/30 bg-info-light/30 p-3.5" data-testid="backup-gateway-note">
              <p className="text-xs text-text-primary leading-relaxed">
                Some cards will automatically fall back to a backup processor if the primary declines.
              </p>
            </div>
          )}

          <div className="mt-4 rounded-xl border border-warning-main/40 bg-warning-light/30 p-3.5">
            <p className="text-xs font-medium text-warning-main">
              Once approved, these payments are queued for charging and can't be pulled back.
            </p>
          </div>
        </div>

        {needsTypedCount && (
          <TypedCheck
            id="approve-typed-count"
            className="px-6 pt-4 border-t border-divider flex flex-col gap-1"
            label={`Type ${count} to confirm how many payments you approve`}
            value={typedCount}
            onChange={setTypedCount}
            disabled={submitting}
          />
        )}
        <div className="px-6 py-4 border-t border-divider flex gap-3">
          <Button variant="outlined" fullWidth data-testid="cancel-process-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            data-testid="confirm-process-btn"
            onClick={onConfirm}
            disabled={submitting || count === 0 || !typedCountMatches}
          >
            {submitting ? 'Approving…' : 'Approve for Processing'}
          </Button>
        </div>
      </div>
    </Drawer>
  )
}

export default ConfirmDrawer
